import {
  applyAction,
  startMatch,
  tally,
  watchFinalTallies,
  type AppraisalTarget,
  type ItemSpec,
  type MatchState,
  type TallyInput,
} from '../../../engine';
import type { NightMatch } from '../../../campaign';

/**
 * What an item would have added (docs/LONG_NIGHT_ART_DIRECTION.md §11.9, docs/SHOP_DESIGN.md §7):
 * the last table is replayed, its best chain of yours caught as it was scored, and recounted
 * with the item considered (a Superstition on the Mantel's right end, or an Appraisal's level).
 * Nothing makes an item more desirable than your own chain counted with it.
 */

export interface ChainProbe {
  /** The best chain's score as it was counted. */
  readonly score: number;
  /** Its score with this added: a Superstition, or an Appraisal level. */
  readonly with: (add: { superstition?: ItemSpec; appraisal?: AppraisalTarget }) => number;
}

export function probeBestChain(m: NightMatch | null | undefined): ChainProbe | null {
  if (!m) return null;
  let best: { s: MatchState; input: TallyInput; score: number } | null = null;
  watchFinalTallies((s, input) => {
    if (input.side !== 'S') return;
    const score = tally(s, input, 'preview').breakdown.score;
    if (!best || score > best.score)
      best = { s: structuredClone(s), input: structuredClone(input), score };
  });
  try {
    let s = startMatch(m.config, m.seed).state;
    for (const a of m.actions) {
      const r = applyAction(s, a);
      if (r.error) break;
      s = r.state;
    }
  } catch {
    best = null;
  } finally {
    watchFinalTallies(null);
  }
  const found = best as { s: MatchState; input: TallyInput; score: number } | null;
  if (!found) return null;
  return {
    score: found.score,
    with: ({ superstition, appraisal }) => {
      let s = found.s;
      const l = s.ledger;
      if (superstition && l)
        s = { ...s, ledger: { ...l, mantel: { ...l.mantel, S: [...l.mantel.S, superstition] } } };
      if (appraisal && s.config.ledger) {
        const kit = s.config.ledger.kits.S;
        s = {
          ...s,
          config: {
            ...s.config,
            ledger: {
              ...s.config.ledger,
              kits: {
                ...s.config.ledger.kits,
                S: {
                  ...kit,
                  appraisals: {
                    ...kit.appraisals,
                    [appraisal]: (kit.appraisals[appraisal] ?? 1) + 1,
                  },
                },
              },
            },
          },
        };
      }
      try {
        return tally(s, found.input, 'preview').breakdown.score;
      } catch {
        return found.score;
      }
    },
  };
}
